import {PropsWithChildren, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Button, Collapse, Divider, Stack, Typography} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {ActionButton} from "../base/ActionButton";

export interface Props extends PropsWithChildren {
    header: string;
    date: string;
    isPending: boolean;
    isLoading: boolean;
    error: string | undefined;
    onAccept?: () => void;
    onReject?: () => void;
    children?: React.ReactNode;
    collapseOnEnterCallback?: (props: Props) => void;
}

export const NotificationCard: React.FC<Props> = (props) => {
    const {t} = useTranslation();
    const {header, date, isPending, isLoading, error, onAccept, onReject, children, collapseOnEnterCallback} = props;
    const [open, setOpen] = useState<boolean>(false);

    return (
        <Stack direction="column" width="100%" border={1} borderColor="#E9EBF1">
            <Button
                variant="text"
                onClick={() => setOpen(!open)}
                sx={{
                    padding: 0,
                    margin: 0,
                    textTransform: "none",
                    border: 0,
                }}
            >
                <Stack
                    direction="row"
                    paddingX="1.5rem"
                    height="3.5rem"
                    width="100%"
                    alignItems="center"
                    justifyContent="space-between"
                    bgcolor="#F3F5FA"
                >
                    <Stack direction="row" gap="1rem" alignItems="center">
                        {open ? (
                            <ExpandMoreIcon
                                sx={{
                                    color: "#6476CB",
                                    width: "1.4rem",
                                    height: "1.4rem",
                                }}
                            />
                        ) : (
                            <ArrowRightIcon
                                sx={{
                                    color: "#6476CB",
                                    width: "1.4rem",
                                    height: "1.4rem",
                                }}
                            />
                        )}
                        <CircleIcon
                            sx={{
                                color: isPending ? "#6476CB" : "#DBDEE6",
                                width: "1rem",
                                height: "1rem",
                            }}
                        />
                        <Typography
                            maxWidth="20rem"
                            fontSize={16}
                            data-test="header"
                            color="#3A3F4F"
                            textOverflow="ellipsis"
                            noWrap
                        >
                            {header}
                        </Typography>
                    </Stack>
                    <Typography fontSize={16} textTransform="uppercase" data-test="date" color="#737990">
                        {date}
                    </Typography>
                </Stack>
            </Button>
            <Collapse
                in={open}
                unmountOnExit
                onEnter={() => {
                    collapseOnEnterCallback?.(props);
                }}
            >
                <Stack direction="column" borderTop={1} borderColor="#E9EBF1">
                    {error && (
                        <Alert severity="error" sx={{paddingX: "3rem"}}>
                            {error}
                        </Alert>
                    )}
                    <Stack direction="column" paddingX="3rem" paddingY="1.5rem" gap="1.5rem">
                        {children ? (
                            <Stack direction="column" justifyContent="center">
                                {children}
                            </Stack>
                        ) : (
                            <>
                                {isPending && onAccept && onReject && (
                                    <>
                                        <Divider color="#E9EBF1" />
                                        <Stack direction="row" justifyContent="end" alignContent="right">
                                            <Stack direction="row" gap="1rem">
                                                <ActionButton
                                                    data-test="rejectNotification"
                                                    variant="contained"
                                                    color="secondary"
                                                    icon="clear"
                                                    onClick={onReject}
                                                    disabled={isLoading}
                                                >
                                                    {t("dialogs.actions.reject")}
                                                </ActionButton>
                                                <ActionButton
                                                    data-test="acceptNotification"
                                                    variant="contained"
                                                    color="primary"
                                                    icon="check"
                                                    onClick={onAccept}
                                                    disabled={isLoading}
                                                >
                                                    {t("dialogs.actions.accept")}
                                                </ActionButton>
                                            </Stack>
                                        </Stack>
                                    </>
                                )}
                            </>
                        )}
                    </Stack>
                </Stack>
            </Collapse>
        </Stack>
    );
};
